/**
 * CR-8227: the details panel under the setup table, showing the row picked
 * with Details. A site gets the same fields the Organisation tree asks for
 * today; a division its name, description and country. Edits apply as typed.
 */
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Trash2 } from 'lucide-react';
import { useCurrencyChoices } from '../../orgchart/useCurrencyChoices';
import type { NodeConfigForm } from '../../orgchart/useNodeConfigForm';
import type { LevelNames, OrganisationShape, SetupRow } from './organisationSetup';

interface OrganisationRowDetailsProps {
  row: SetupRow;
  shape: OrganisationShape;
  labels: LevelNames;
  /** The divisions a site may belong to (three-level structure only). */
  divisions: SetupRow[];
  disabled: boolean;
  /** Why the row cannot be removed, or null when it can. */
  removeBlockedReason: string | null;
  onFieldChange: (key: string, field: keyof NodeConfigForm, value: string) => void;
  onParentChange: (key: string, parentKey: string) => void;
  onRemove: (key: string) => void;
}

type FieldSpec = [field: keyof NodeConfigForm, title: string, type?: string, placeholder?: string];

const SITE_FIELDS_BEFORE_CURRENCY: FieldSpec[] = [
  ['name', 'Name *'],
  ['primaryEmail', 'Primary email *', 'email', 'site@company.com'],
  ['registrationNumber', 'Company number'],
];
const SITE_FIELDS_AFTER_CURRENCY: FieldSpec[] = [
  ['employeeCount', 'Number of employees', 'number'],
  ['yearEstablished', 'Year established', 'number'],
  ['annualRevenue', 'Annual revenue', 'number'],
  ['website', 'Website', 'url', 'https://'],
  ['addressLine1', 'Address line 1'],
  ['addressLine2', 'Address line 2'],
  ['city', 'City'],
  ['stateCounty', 'County'],
  ['postalCode', 'Postcode'],
  ['country', 'Country'],
];
const DIVISION_FIELDS: FieldSpec[] = [
  ['name', 'Name *'],
  ['description', 'Description', 'text', 'Brief description'],
  ['country', 'Country'],
];

/**
 * @param row - the row being edited
 * @param shape - the chosen structure
 * @param labels - level names, for headings and wording
 * @param divisions - the divisions a site may belong to
 * @param disabled - read only while the create run is going, or once created
 * @param removeBlockedReason - shown instead of removing, when set
 * @param onFieldChange - sets one field of the row
 * @param onParentChange - moves a site to another division
 * @param onRemove - removes the row
 */
export function OrganisationRowDetails({
  row, shape, labels, divisions, disabled, removeBlockedReason, onFieldChange, onParentChange, onRemove,
}: OrganisationRowDetailsProps) {
  const currencies = useCurrencyChoices();
  const isSite = row.kind === 'site';
  const label = isSite ? labels.site : labels.division;
  const locked = disabled || Boolean(row.createdId);

  const input = ([field, title, type = 'text', placeholder]: FieldSpec) => (
    <div key={field} className="space-y-1">
      <Label htmlFor={`setup-detail-${field}`} className="font-normal">{title}</Label>
      <Input
        id={`setup-detail-${field}`}
        type={type}
        min={type === 'number' ? 0 : undefined}
        value={String(row.form[field])}
        placeholder={placeholder}
        disabled={locked}
        onChange={(e) => onFieldChange(row.key, field, e.target.value)}
      />
    </div>
  );

  return (
    <div className="space-y-3 rounded-md border bg-muted/20 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="font-semibold">
          {label} details{isSite && ': the same fields the Organisation tree asks for today'}
        </h4>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          disabled={locked || removeBlockedReason !== null}
          title={removeBlockedReason ?? undefined}
          onClick={() => onRemove(row.key)}
        >
          <Trash2 className="mr-1 h-4 w-4" /> Remove {label.toLowerCase()}
        </Button>
      </div>

      {isSite ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {SITE_FIELDS_BEFORE_CURRENCY.map(input)}
          <div className="space-y-1">
            <Label htmlFor="setup-detail-currency" className="font-normal">Currency</Label>
            <Select value={row.form.currency} disabled={locked} onValueChange={(value) => onFieldChange(row.key, 'currency', value)}>
              <SelectTrigger id="setup-detail-currency"><SelectValue placeholder="Select currency" /></SelectTrigger>
              <SelectContent>
                {currencies.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {SITE_FIELDS_AFTER_CURRENCY.map(input)}
          {shape === 'group_divisions_sites' && (
            <div className="space-y-1">
              <Label htmlFor="setup-detail-division" className="font-normal">{labels.division} *</Label>
              <Select value={row.parentKey ?? ''} disabled={locked} onValueChange={(value) => onParentChange(row.key, value)}>
                <SelectTrigger id="setup-detail-division">
                  <SelectValue placeholder={`Choose a ${labels.division.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {divisions.map((division, index) => (
                    <SelectItem key={division.key} value={division.key}>
                      {division.form.name.trim() || `${labels.division} ${index + 1}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {DIVISION_FIELDS.map(input)}
        </div>
      )}

      {isSite && (
        <p className="text-xs text-muted-foreground">
          Everything else about the {label.toLowerCase()} is completed later by the customer admin on the{' '}
          {label.toLowerCase()}'s profile page.
        </p>
      )}
    </div>
  );
}
